import {
  asLink,
  isFilled,
  type LinkField,
  type RTImageNode,
} from '@prismicio/client';

import linkResolver from '@adapters/prismic/utils/linkResolver';

export interface LinkAttributes {
  href: string;
  target?: '_blank';
  rel?: 'noopener noreferrer';
}

const hasCategoryPath = (data: unknown): boolean =>
  typeof data === 'object' &&
  data !== null &&
  'category_path' in data &&
  !!data.category_path;

// `linkResolver` builds a collection URL from `data.category_path`, which is
// only there when the page fetcher asked for it (`LINK_RESOLVER_FETCH_LINKS`),
// and `asLink` calls the resolver before looking at `url`. So a collection
// link without the path skips the resolver: it uses a pre-resolved `url` if
// it has one, and is unresolved otherwise (the resolver would throw).
const isCollectionWithoutPath = (field: LinkField): boolean =>
  field.link_type === 'Document' &&
  'type' in field &&
  field.type === 'collection' &&
  !hasCategoryPath('data' in field ? field.data : undefined);

// Prismic keeps a link to a deleted or unpublished document but marks it
// `isBroken`; `asLink` would still build a URL to it, which 404s.
const isBroken = (field: LinkField): boolean =>
  'isBroken' in field && field.isBroken === true;

/**
 * `<a>` attributes for a Prismic link: text hyperlinks and an image's own
 * link alike. Document links go through `linkResolver`, so they get the
 * locale; new-tab links get `rel="noopener noreferrer"`. Returns `null` for
 * an empty link, a link to a deleted or unpublished document, or a
 * collection link whose path wasn't fetched: none has anywhere to go, so the
 * content renders without an anchor.
 */
export const linkAttributes = (
  field: LinkField | null | undefined
): LinkAttributes | null => {
  if (!field || !isFilled.link(field) || isBroken(field)) return null;

  const href = isCollectionWithoutPath(field)
    ? ('url' in field && field.url) || null
    : asLink(field, { linkResolver });
  if (!href) return null;

  return 'target' in field && field.target === '_blank'
    ? { href, target: '_blank', rel: 'noopener noreferrer' }
    : { href };
};

/**
 * `linkAttributes` for an image's own Link field. A linked image's alt text
 * is the link's only accessible name, so an empty alt makes a link screen
 * readers can't describe. There's no honest text to substitute, so in
 * development it warns, to be caught in preview.
 */
export const imageLinkAttributes = (
  image: Pick<RTImageNode, 'alt' | 'url'> & { linkTo?: LinkField | null }
): LinkAttributes | null => {
  const link = linkAttributes(image.linkTo);

  if (link && !image.alt?.trim() && process.env.NODE_ENV !== 'production') {
    console.warn(
      `RichText: linked image ${image.url} has no alt text, so its link has no accessible name. Add alt text in Prismic.`
    );
  }

  return link;
};
